import { useState } from "react";
import { MapPin } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { EVENTS, CONTACT_EMAIL } from "@/data/events";

export default function Eventos() {
  const { t, lang } = useLang();
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: 1 });
  const events = EVENTS;

  const submit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Registro a evento: ${selected.title[lang]}`);
    const body = encodeURIComponent(
      `Evento / Event: ${selected.title.es} / ${selected.title.en}\n` +
      `Horario / Time: ${selected.time[lang]}\n` +
      `Lugar / Location: ${selected.location}\n\n` +
      `Nombre / Name: ${form.name}\nEmail: ${form.email}\n` +
      `Teléfono / Phone: ${form.phone || "-"}\nAsistentes / Guests: ${form.guests}`
    );
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    toast.success(t.eventos.successTitle, { description: t.eventos.mailNote });
    setSelected(null);
    setForm({ name: "", email: "", phone: "", guests: 1 });
  };

  return (
    <div data-testid="eventos-page">
      <section className="pt-40 pb-20 max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <Overline>{t.eventos.overline}</Overline>
          <h1 className="font-display text-5xl md:text-7xl font-light tracking-tight mt-6">
            {t.eventos.title}
          </h1>
          <p className="text-white/60 text-lg mt-8 max-w-xl leading-relaxed">{t.eventos.sub}</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <div className="flex flex-col">
          {events.map((ev, i) => {
            const d = new Date(ev.date + "T12:00:00");
            return (
              <Reveal key={ev.id} delay={i * 0.05}>
                <div
                  className="group grid grid-cols-1 md:grid-cols-12 gap-6 items-center border-t border-white/10 py-10 hover:bg-coal/60 transition-colors duration-500 px-4 md:px-6"
                  data-testid={`event-row-${ev.id}`}
                >
                  <div className="md:col-span-2">
                    <p className="font-display text-5xl font-light text-ember">{d.getDate()}</p>
                    <p className="text-xs uppercase tracking-[0.25em] text-white/50 mt-1">
                      {d.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { month: "long", year: "numeric" })}
                    </p>
                  </div>
                  <div className="md:col-span-6">
                    <span className="inline-block text-[10px] uppercase tracking-[0.25em] border border-ember/40 text-sand rounded-full px-3 py-1 mb-3">
                      {t.eventos.categories[ev.category] || ev.category}
                    </span>
                    <h3 className="font-display text-3xl font-light mb-2">{ev.title[lang]}</h3>
                    <p className="text-sm text-white/60 mb-2">{ev.description[lang]}</p>
                    <p className="text-xs text-white/40 flex items-center gap-2">
                      <MapPin size={12} className="text-ember" /> {ev.location} · {ev.time[lang]}
                    </p>
                  </div>
                  <div className="md:col-span-4 md:text-right">
                    <button
                      data-testid={`event-register-btn-${ev.id}`}
                      onClick={() => setSelected(ev)}
                      className="text-xs uppercase tracking-[0.2em] border border-white/30 rounded-full px-8 py-3.5 hover:bg-ember hover:border-ember transition-colors duration-300"
                    >
                      {t.eventos.register}
                    </button>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="bg-coal border-white/10 text-white" data-testid="event-register-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-3xl font-light text-white">
                  {selected.title[lang]}
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-white/60">{selected.time[lang]} · {selected.location}</p>
              <form onSubmit={submit} className="flex flex-col gap-5 mt-4">
                <Input
                  required
                  data-testid="event-form-name"
                  placeholder={t.eventos.form.name}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-ember placeholder:text-white/40"
                />
                <Input
                  required
                  type="email"
                  data-testid="event-form-email"
                  placeholder={t.eventos.form.email}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-ember placeholder:text-white/40"
                />
                <Input
                  data-testid="event-form-phone"
                  placeholder={t.eventos.form.phone}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 focus-visible:ring-0 focus-visible:border-ember placeholder:text-white/40"
                />
                <label className="flex items-center gap-4 text-sm text-white/60">
                  {t.eventos.form.guests}
                  <select
                    data-testid="event-form-guests"
                    value={form.guests}
                    onChange={(e) => setForm({ ...form, guests: e.target.value })}
                    className="bg-coal border border-white/20 rounded px-3 py-2 text-white"
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>
                <button
                  type="submit"
                  data-testid="event-form-submit"
                  className="mt-2 bg-ember text-black text-xs uppercase tracking-[0.2em] font-bold rounded-full px-8 py-4 hover:bg-white transition-colors duration-300"
                >
                  {t.eventos.form.submit}
                </button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
